import { showErrorMessage } from "@/composition/useLoading"
import { watchImmediate } from "@vueuse/core"
import { ref, shallowRef, toRef, watch, type FormHTMLAttributes } from "vue"
import {
	LoadingError,
	ValidationHandler,
	defineComponent,
	inferProps,
	renderSlot,
	useCustomFormValidation,
	type Component,
	type ReactiveComponent,
	type ValidationResult,
} from "vue-utils"
import type { BootstrapButtonType } from "./BootstrapButton"
import BootstrapButton from "./BootstrapButton"
import FormButtons from "./FormButtons"
import FormMissingInfo from "./FormMissingInfo"

export interface FormButton {
	id: string
	onSubmit: (event: Event) => void | Promise<void>
	validate?: () => ValidationResult | Promise<ValidationResult>
	validateForm?: boolean

	render: Component<{
		onClick: (e: UIEvent) => void
		disabled: boolean
		isSubmitting: boolean
	}>
}

interface Props {
	buttons: FormButton[]

	updateRef?(form: HTMLFormElement | undefined): void
	onSetupValidation?(handler: ValidationHandler): void
}

export const basicFormButton = (
	options: Omit<FormButton, "render"> & { color: BootstrapButtonType; text: string }
): FormButton => {
	const { color, text, ...buttonOptions } = options
	return {
		render: (props) => (
			<BootstrapButton color={color} {...props}>
				{text}
			</BootstrapButton>
		),
		...buttonOptions,
	}
}

const BasicForm: ReactiveComponent<Props, FormHTMLAttributes> = (props, { attrs, slots }) => {
	const formRef = ref<HTMLFormElement>()
	const currentErrors = shallowRef<string[] | null>(null)
	const buttonSubmitting = shallowRef<FormButton | null>(null)

	const validation = useCustomFormValidation({ form: formRef })

	async function runSubmit(event: Event, button: FormButton) {
		buttonSubmitting.value = button
		try {
			await button.onSubmit(event)
		} catch (e) {
			if (e instanceof LoadingError) {
				void showErrorMessage(e.title, e.message)
			} else {
				void showErrorMessage("Unexpected Error", (e as Error).message)
				console.error(e)
			}
		} finally {
			buttonSubmitting.value = null
		}
	}

	async function handleSubmit(event: Event, button: FormButton) {
		event.preventDefault()
		event.stopImmediatePropagation()
		if (buttonSubmitting.value !== null) {
			return
		}
		const validationResult = await validateButton(button)
		if (validationResult === true) {
			await runSubmit(event, button)
			return
		}
		if (typeof validationResult === "string") {
			currentErrors.value = [validationResult]
		} else if (Array.isArray(validationResult)) {
			currentErrors.value = validationResult
		}
	}

	async function validateButton(button: FormButton) {
		if (button.validateForm !== false) {
			const result = await validation.validateForm()
			if (!result.successful) {
				if (result.errorMessages && result.errorMessages.length > 0) {
					return result.errorMessages
				}
				return false
			}
		}
		if (button.validate) {
			return await button.validate()
		}
		return true
	}

	watch(formRef, (formEl) => props.updateRef?.(formEl))
	watchImmediate(toRef(props, "onSetupValidation"), (setupVal) => setupVal?.(validation))

	return () => (
		<form
			ref={formRef}
			class="basic-form"
			{...attrs}
			onSubmit={(e) => {
				e.preventDefault()
				e.stopImmediatePropagation()
				return false
			}}
		>
			{
				//https://stackoverflow.com/questions/895171/prevent-users-from-submitting-a-form-by-hitting-enter
			}
			<button type="submit" disabled style="display: none" aria-hidden="true" />

			{renderSlot(slots)}
			<hr class="w-full" />
			<FormButtons buttons={props.buttons.length}>
				{props.buttons.map((button) => (
					<button.render
						key={button.id}
						disabled={buttonSubmitting.value !== null}
						isSubmitting={buttonSubmitting.value !== null && buttonSubmitting.value.id === button.id}
						onClick={(e) => void handleSubmit(e, button)}
					/>
				))}
			</FormButtons>
			{currentErrors.value && (
				<FormMissingInfo messages={currentErrors.value} confirm={() => (currentErrors.value = null)} />
			)}
		</form>
	)
}

export default defineComponent(BasicForm, inferProps<Props>())
